import React, {
  CSSProperties,
  KeyboardEvent,
  PointerEvent,
  ReactNode,
  useId,
  useRef,
} from 'react';
import { ScoreBand } from '../types';
import {
  normalizeSliderValue,
  sliderPositionToValue,
  valueToSliderPosition,
} from '../utils/segmentedSlider';

interface VitalInputProps {
  id: string;
  label: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  bands: ScoreBand[];
  step?: number;
  controls?: ReactNode;
  variant?: 'default' | 'oxygen';
  onChange: (value: number) => void;
}

type MarkerStyle = CSSProperties & {
  '--band-count': number;
  '--marker-left': string;
  '--marker-color': string;
};

export const getScoreColor = (score: number): string => {
  if (score === 0) return '#19c37d';
  if (score === 1) return '#f3c623';
  if (score === 2) return '#ff7a1a';
  return '#ff334e';
};

const VitalInput: React.FC<VitalInputProps> = ({
  id,
  label,
  unit,
  value,
  min,
  max,
  bands,
  step = 1,
  controls,
  variant = 'default',
  onChange,
}) => {
  const descriptionId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const currentBand = bands.find((band) => {
    const meetsMinimum = band.min === undefined || value >= band.min;
    const meetsMaximum = band.max === undefined || value <= band.max;
    return meetsMinimum && meetsMaximum;
  });
  const markerStyle: MarkerStyle = {
    '--band-count': bands.length,
    '--marker-left': `${valueToSliderPosition(value, min, max, bands)}%`,
    '--marker-color': getScoreColor(currentBand?.score ?? 0),
  };

  const updateFromInput = (rawValue: string) => {
    const parsedValue = Number.parseFloat(rawValue);
    if (Number.isFinite(parsedValue)) onChange(parsedValue);
  };

  const updateFromPointer = (clientX: number) => {
    const track = trackRef.current;
    if (!track) return;

    const { left, width } = track.getBoundingClientRect();
    if (width <= 0) return;

    const position = ((clientX - left) / width) * 100;
    onChange(sliderPositionToValue(position, min, max, bands, step));
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromPointer(event.clientX);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    updateFromPointer(event.clientX);
  };

  const releasePointer = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleSliderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let nextValue: number | undefined;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      nextValue = value - step;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      nextValue = value + step;
    } else if (event.key === 'Home') {
      nextValue = min;
    } else if (event.key === 'End') {
      nextValue = max;
    }

    if (nextValue === undefined) return;
    event.preventDefault();
    onChange(normalizeSliderValue(nextValue, min, max, step));
  };

  return (
    <div className={`vital-row${variant === 'oxygen' ? ' vital-row--oxygen' : ''}`}>
      <div className="vital-row__label">
        <label htmlFor={`${id}-number`}>{label}</label>
        <span id={descriptionId}>{unit}</span>
      </div>

      <div className="vital-row__value">
        <input
          id={`${id}-number`}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={step < 1 ? value.toFixed(1) : value}
          aria-describedby={descriptionId}
          onChange={(event) => updateFromInput(event.target.value)}
        />
      </div>

      <div className="vital-row__scale">
        {controls}
        <div className="score-scale" style={markerStyle}>
          <div className="score-scale__scores" aria-hidden="true">
            {bands.map((band) => (
              <span key={`${band.label}-${band.score}`}>{band.score}</span>
            ))}
          </div>
          <div
            className="score-slider"
            role="slider"
            tabIndex={0}
            aria-label={`${label}, slankiklis`}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-valuetext={`${value} ${unit}, ${currentBand?.score ?? 0} NEWS2 balai`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={releasePointer}
            onPointerCancel={releasePointer}
            onKeyDown={handleSliderKeyDown}
          >
            <div ref={trackRef} className="score-scale__track" aria-hidden="true">
              {bands.map((band) => (
                <span
                  key={`${band.label}-${band.score}`}
                  style={{ backgroundColor: getScoreColor(band.score) }}
                />
              ))}
              <span className="score-scale__marker" />
            </div>
          </div>
          <div className="score-scale__labels" aria-hidden="true">
            {bands.map((band) => (
              <span key={`${band.label}-${band.score}`}>{band.label}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VitalInput;
